import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { ApiBaseUrls, ApiControllers } from "../../constants/constants";
import { ServiceResultModel } from "../../models/service/service-result.model";
import { ServiceUtilities } from "../../utilities/service.utilities";
import { APISanitizerUtilities } from "../../utilities/api-sanitizer.utilities";
import { PaginatedListModel } from "../../models/service/paginated-list.model";
import { PaginationResponseHeader } from "../../models/service/pagination-response-header.model";
import { ChartOfAccountsFilter } from "../../filters/chart-of-accounts.filter";
import {
  ChartOfAccountModel,
  CreateChartOfAccountRequest,
  UpdateChartOfAccountRequest
} from "../../models/data/chart-of-account.model";
import { ChartOfAccountAuditLogModel } from "../../models/data/chart-of-account-audit-log.model";
import { AuditLog } from "../../models/service/audit-log.model";
import { AuditLogMapper } from "../../utilities/audit-log-mapper.utilities";

@Injectable({
  providedIn: 'root'
})
export class ChartOfAccountService {
  private chartOfAccountsApiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.CHART_OF_ACCOUNTS()}`;
  private organizationsApiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.ORGANIZATIONS()}`;

  constructor(private httpClient: HttpClient) { }

  public async createChartOfAccount(request: CreateChartOfAccountRequest): Promise<ServiceResultModel<ChartOfAccountModel>> {
    try {
      const sanitizedPayload = APISanitizerUtilities.sanitizePayload(request);
      const response = await firstValueFrom(
        this.httpClient.post<ChartOfAccountModel>(
          this.chartOfAccountsApiPath,
          sanitizedPayload,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const account = <ChartOfAccountModel>response.body?.valueOf();
      return <ServiceResultModel<ChartOfAccountModel>>{ data: account, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getChartOfAccountById(chartOfAccountId: number): Promise<ServiceResultModel<ChartOfAccountModel>> {
    try {
      const requestUrl = `${this.chartOfAccountsApiPath}/${chartOfAccountId}`;
      const response = await firstValueFrom(
        this.httpClient.get<ChartOfAccountModel>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const account = <ChartOfAccountModel>response.body?.valueOf();
      return <ServiceResultModel<ChartOfAccountModel>>{ data: account, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateChartOfAccount(
    chartOfAccountId: number,
    request: UpdateChartOfAccountRequest
  ): Promise<ServiceResultModel<ChartOfAccountModel>> {
    try {
      const requestUrl = `${this.chartOfAccountsApiPath}/${chartOfAccountId}`;
      const sanitizedPayload = APISanitizerUtilities.sanitizePayload(request);
      const response = await firstValueFrom(
        this.httpClient.patch<ChartOfAccountModel>(
          requestUrl,
          sanitizedPayload,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const account = <ChartOfAccountModel>response.body?.valueOf();
      return <ServiceResultModel<ChartOfAccountModel>>{ data: account, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateChartOfAccountStatus(
    chartOfAccountId: number,
    active: boolean,
    note?: string
  ): Promise<ServiceResultModel<ChartOfAccountModel>> {
    try {
      const requestUrl = `${this.chartOfAccountsApiPath}/${chartOfAccountId}/status?active=${active}`;
      const response = await firstValueFrom(
        this.httpClient.patch<ChartOfAccountModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<string | undefined>(note)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const account = <ChartOfAccountModel>response.body?.valueOf();
      return <ServiceResultModel<ChartOfAccountModel>>{ data: account, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async searchChartOfAccounts(
    filter: ChartOfAccountsFilter,
    orderBy?: string,
    skip?: number,
    take?: number
  ): Promise<ServiceResultModel<PaginatedListModel<ChartOfAccountModel>>> {
    try {
      const encodedOrderBy = orderBy ? encodeURIComponent(orderBy) : '';
      const requestUrl = `${this.chartOfAccountsApiPath}/filter?orderBy=${encodedOrderBy}&skip=${skip ?? 0}&take=${take ?? ''}`;

      const response = await firstValueFrom(
        this.httpClient.post<ChartOfAccountModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<ChartOfAccountsFilter>(filter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<ChartOfAccountModel> = {
        data: <ChartOfAccountModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  // Chart-of-accounts audit logs are organization-scoped, not per-account — the live API has no
  // per-account logs route. Callers (ms-audit-logs) pass the organization id as the entity id.
  public async getChartOfAccountAuditLogs(
    organizationId: number,
    skip: number,
    take: number,
    includeDetails: boolean = false
  ): Promise<ServiceResultModel<PaginatedListModel<AuditLog>>> {
    try {
      // Cache-busting timestamp: this log lives under the organizations controller while the mutations
      // that create new entries are on the chart-of-accounts controller, so relying on the caching
      // interceptor's controller-name invalidation to catch this cross-controller case proved unreliable.
      // A unique URL per call guarantees a real network fetch every time the activity log is opened.
      let requestUrl = `${this.organizationsApiPath}/${organizationId}/chart-of-accounts-logs?skip=${skip}&take=${take}&_=${Date.now()}`;
      if (includeDetails) {
        requestUrl += `&includeDetails=true`;
      }
      const response = await firstValueFrom(
        this.httpClient.get<ChartOfAccountAuditLogModel[]>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const mappedData = AuditLogMapper.mapChartOfAccountAuditLogs(<ChartOfAccountAuditLogModel[]>response.body?.valueOf());

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<AuditLog> = {
        data: mappedData,
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }
}
